import {Chips, DatePicker, Separator, useTranslationContext} from 'platform/components';
import {Show, Space, VStack} from 'platform/foundation';

import {getApiDateString, suffixTestId} from 'shared/web';

import {EagFilterProps} from '../../types/EagFilterProps';
import {parseDateFilter} from '../../utils/parseDateFilter';
import {useDateRangeFilter} from '../../utils/useDateRangeFilter';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';
import {RelativeInputsSection} from '../RelativeInputsSection/RelativeInputsSection';

type DateRangeFilterProps = EagFilterProps & {
  min: string | null;
  max: string | null;
  relativeDates: Array<{value: string; label: string}>;
  relativeInputs: Array<{value: string; label: string}>;
  quickFilters?: Array<{value: string; label: string}>;
};

export function DateRangeFilter(props: DateRangeFilterProps) {
  const t = useTranslationContext();

  // FIX: previously min/max were declared but never parsed/passed (latent bug).
  const minDate = parseDateFilter(props.min);
  const maxDate = parseDateFilter(props.max);

  const filter = useDateRangeFilter({
    value: props.value,
    onChange: props.onChange,
    dateFormatter: getApiDateString,
    hasRelativeInputs: true,
  });

  return (
    <VStack spacing={2}>
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={filter.quickFilterValue}
        defaultValue={null}
        setFilterValue={filter.setFilterValue}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('quickFilters-chips', props)}
      />
      <Show when={props.relativeDates && props.relativeDates.length > 0}>
        <>
          <Chips
            isDisabled={props.isDisabled}
            value={filter.chipsValue}
            options={props.relativeDates}
            onChange={filter.onChipsChange}
            isMultiple={false}
            data-testid={suffixTestId('relativeDates-chips', props)}
            isDeselectable
          />
          <Separator />
        </>
      </Show>
      <DatePicker
        label={t('page.datagrid.filter.dateLabelFrom')}
        value={filter.fromPickerValue}
        onChange={filter.onDateFromChange}
        minDate={minDate}
        maxDate={maxDate}
        data-testid={suffixTestId('date-range-filter-from', props)}
        stackDirection="column"
      />
      <Space vertical={2} />
      <DatePicker
        label={t('page.datagrid.filter.dateLabelTo')}
        value={filter.toPickerValue}
        onChange={filter.onDateToChange}
        minDate={minDate}
        maxDate={maxDate}
        data-testid={suffixTestId('date-range-filter-to', props)}
        stackDirection="column"
      />
      <RelativeInputsSection
        relativeInputs={props.relativeInputs}
        filter={filter}
        isDisabled={props.isDisabled}
        getInputTestId={(label) => suffixTestId(`relativeInput-${label}`, props)}
      />
    </VStack>
  );
}
